import React, { FC, useState, useEffect } from 'react';
import Select from 'react-select';
import { useIntl } from "react-intl";
import { IProductProps, productActionType } from '../../cors/reducers';
import { getColors, getSizes } from "../../cors/_requests";
import SharedData from "../../../common/SharedData";
import '../../../../stylesheet/BarcodeField.css';
import '../../../../stylesheet/CategoriesForm.css';
import { IColorsModel, ISizesModel } from "../../cors/_models";
import '../../../../stylesheet/VariationsField.css';
import { toAbsoluteUrl } from "../../../../../_metronic/helpers";
import "../../../../stylesheet/imagesPopup.css";
import ProductMoreImagesVariations from "./ProductMoreImagesVariations";

interface Variation {
    barcode: string;
    price: number;
    quantity: number;
    color_id: number;
    size_id: number;
}

type VariationField = 'barcode' | 'price' | 'quantity' | 'color_id' | 'size_id';

const VariationsField: FC<IProductProps> = ({ prodState, prodDispatch }) => {
    // Hooks for internationalization and shared data
    const { formatMessage } = useIntl();
    const { currentUser, language } = SharedData();

    // Component state declarations
    const [variations, setVariations] = useState<Variation[]>([]);
    const [availableColors, setAvailableColors] = useState<{ label: string; value: number }[]>([]);
    const [availableSizes, setAvailableSizes] = useState<{ label: string; value: number }[]>([]);
    const [selectedColors, setSelectedColors] = useState<string[]>([]);
    const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
    const [isChecked1, setIsChecked1] = useState(false);
    const [isChecked2, setIsChecked2] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [activePopupIndex, setActivePopupIndex] = useState<number | null>(null);
    const [activeBarcodeField, setActiveBarcodeField] = useState(false);

    // Fetch colors and sizes, and initialize variations if they exist
    useEffect(() => {
        if (prodState.variations && prodState.variations.length > 0) {
            const updatedVariations = prodState.variations.map((variation) => ({
                ...variation,
                price: parseFloat(variation.price),
                quantity: parseInt(variation.quantity, 10),
                color_id: parseInt(variation.color_id, 10),
                size_id: parseInt(variation.size_id, 10),
            }));
            setVariations(updatedVariations);
            setShowTable(true);

            const colors = updatedVariations
                .map((variation) => variation.color_id.toString())
                .filter((color) => color !== "-1");

            const sizes = updatedVariations
                .map((variation) => variation.size_id.toString())
                .filter((size) => size !== "-1");

            setSelectedColors(Array.from(new Set(colors)));
            setSelectedSizes(Array.from(new Set(sizes)));

        }

        const fetchColors = async () => {
            const { data } = await getColors(currentUser?.token, language);
            const colorOptions = data.map((item: IColorsModel) => ({
                label: item.name,
                value: item.color_id
            }));
            setAvailableColors(colorOptions);
        };
        const fetchSizes = async () => {
            const { data } = await getSizes(currentUser?.token, language);
            const sizesOptions = data.map((item: ISizesModel) => ({
                label: item.name,
                value: item.size_id
            }));
            setAvailableSizes(sizesOptions);
        };
        fetchColors();
        fetchSizes();
    }, [currentUser?.token, language, prodState.variations]);


    const generateBarcode = (index: number) => {
        const product_type = '1';
        const currDate = (Math.floor(Date.now() / 100) % Math.pow(10, 10)).toString();
        const checksum = calculateChecksum(product_type + currDate);

        const generatedBarcode = product_type + currDate + checksum;

        const updatedVariations = [...variations];
        updatedVariations[index].barcode = generatedBarcode;
        prodDispatch({
            type: productActionType.updateVariations,
            payload: updatedVariations
        });
        setActiveBarcodeField(true)
        setVariations(updatedVariations);
    };

    const calculateChecksum = (digits: string): number => {
        const odd_sum = parseInt(digits[0]) + parseInt(digits[2]) + parseInt(digits[4]) + parseInt(digits[6]) + parseInt(digits[8]) + parseInt(digits[10]);
        const even_sum = parseInt(digits[1]) + parseInt(digits[3]) + parseInt(digits[5]) + parseInt(digits[7]) + parseInt(digits[9]);
        const total_sum = odd_sum * 3 + even_sum;
        const mod_10 = total_sum % 10;
        return mod_10 === 0 ? 0 : 10 - mod_10;
    };
    const updateVariationsTable = (colors: string[], sizes: string[]) => {
        const rebuiltVariations: Variation[] = [];

        // If we have both colors and sizes
        if (colors.length > 0 && sizes.length > 0) {
            colors.forEach((color) => {
                const colorId = parseInt(color);
                if (colorId !== -1) {
                    sizes.forEach((size) => {
                        const sizeId = parseInt(size);
                        if (sizeId !== -1) {
                            const existingVariation = variations.find(
                                (variation) =>
                                    variation.color_id === colorId && variation.size_id === sizeId
                            );
                            rebuiltVariations.push(existingVariation || {
                                barcode: '',
                                price: 0,
                                quantity: 0,
                                color_id: colorId,
                                size_id: sizeId,
                            });
                        }
                    });
                }
            });
        }
        // If we only have colors
        else if (colors.length > 0) {
            colors.forEach((color) => {
                const colorId = parseInt(color);
                if (colorId !== -1) {
                    const existingVariation = variations.find(
                        (variation) => variation.color_id === colorId
                    );
                    rebuiltVariations.push(existingVariation || {
                        barcode: '',
                        price: 0,
                        quantity: 0,
                        color_id: colorId,
                        size_id: -1,
                    });
                }
            });
        }
        // If we only have sizes
        else if (sizes.length > 0) {
            sizes.forEach((size) => {
                const sizeId = parseInt(size);
                if (sizeId !== -1) {
                    const existingVariation = variations.find(
                        (variation) => variation.size_id === sizeId
                    );
                    rebuiltVariations.push(existingVariation || {
                        barcode: '',
                        price: 0,
                        quantity: 0,
                        color_id: -1,
                        size_id: sizeId,
                    });
                }
            });
        }

        prodDispatch({
            type: productActionType.updateVariations,
            payload: rebuiltVariations,
        });

        setVariations(rebuiltVariations); // Update local state
    };

    // Change handlers for react-select
    const handleColorSelectChange = (selectedOption: any) => {
        if (selectedOption && !selectedColors.includes(selectedOption.value.toString())) {
            const updatedColors = [...selectedColors, selectedOption.value.toString()];
            setShowTable(true);
            setSelectedColors(updatedColors);
            setIsChecked1(true);
            updateVariationsTable(updatedColors, selectedSizes);
        }
    };

    const handleSizeSelectChange = (selectedOption: any) => {
        if (selectedOption && !selectedSizes.includes(selectedOption.value.toString())) {
            const updatedSizes = [...selectedSizes, selectedOption.value.toString()];
            setShowTable(true);
            setSelectedSizes(updatedSizes);
            setIsChecked2(true);
            updateVariationsTable(selectedColors, updatedSizes);
        }
    };

    const removeVariation = (index: number) => {
        const updatedVariations = variations.filter((_, i) => i !== index);
        let deletedVariation = variations[index];

        const isColorUsedInOtherVariations = updatedVariations.some(
            (variation) => variation.color_id === deletedVariation.color_id
        );
        const isSizeUsedInOtherVariations = updatedVariations.some(
            (variation) => variation.size_id === deletedVariation.size_id
        );

        if (!isColorUsedInOtherVariations) {
            const colorToRemove = availableColors.find((color) => color.value === deletedVariation.color_id)?.label;
            if (colorToRemove) {
                removeColor(colorToRemove)
            }
        }
        if (!isSizeUsedInOtherVariations) {
            const sizeToRemove = availableSizes.find((size) => size.value === deletedVariation.size_id)?.label;
            if (sizeToRemove) {
                removeSize(sizeToRemove)
            }
        }

        if (updatedVariations.length === 0) {
            setShowTable(false);
        }

        setVariations(updatedVariations);
        prodDispatch({
            type: productActionType.updateVariations,
            payload: updatedVariations,
        });
    };

    const removeColor = (colorId: string) => {
        const updatedVariations = variations.filter(variation => variation.color_id !== parseInt(colorId));
        if (selectedColors.length === 0 && selectedSizes.length === 0) {
            setShowTable(false);
        }

        const updatedColors = selectedColors.filter(c => c !== colorId);
        setVariations(updatedVariations);
        setSelectedColors(updatedColors);
        updateVariationsTable(updatedColors, selectedSizes);
        prodDispatch({
            type: productActionType.updateVariations,
            payload: updatedVariations
        });
        if (updatedVariations.length === 0){
            setShowTable(false)
        }
        if (selectedColors.length === 0) {
            setSelectedSizes([]);
        }
    };


    const removeSize = (sizeId: string) => {
        const updatedVariations = variations.filter(variation => variation.size_id !== parseInt(sizeId));

        if (selectedColors.length === 0 && selectedSizes.length === 0) {
            setShowTable(false);
        }

        const updatedSizes = selectedSizes.filter(s => s !== sizeId);
        setVariations(updatedVariations);
        setSelectedSizes(updatedSizes);
        updateVariationsTable(selectedColors, updatedSizes);
        prodDispatch({
            type: productActionType.updateVariations,
            payload: updatedVariations
        });

        if (updatedVariations.length === 0){
            setShowTable(false)
        }
    };


    const handleChangeBarcode = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { value } = e.target;
        const updatedVariations = [...variations];
        updatedVariations[index].barcode = value;
        prodDispatch({
            type: productActionType.updateVariations,
            payload: updatedVariations
        });
        setVariations(updatedVariations);
    };


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number, field: VariationField) => {
        const { value } = e.target;
        const updatedVariations = [...variations];

        if (field === 'price' || field === 'quantity' || field === 'color_id' || field === 'size_id') {
            updatedVariations[index][field] = Number(value);
        } else {
            updatedVariations[index][field] = value;
        }

        setVariations(updatedVariations);
        prodDispatch({
            type: productActionType.updateVariations,
            payload: updatedVariations
        });
    };
    const openPopup = (index: number) => {
        setActivePopupIndex(index);
        setIsPopupVisible(true);
    };

    const closePopup = () => {
        setIsPopupVisible(false);
        setActivePopupIndex(null);
    };

    return (
        <div id='variations'>
            <div className='mb-5'>
                <div>{formatMessage({ id: "variations_attributes" })}</div>
            </div>

            <div className='d-flex mb-5' style={{ gap: '30px' }}>
                <div className='d-flex' style={{ gap: '7px' }}>
                    <input
                        type="checkbox"
                        id="colorCheckbox"
                        checked={isChecked1}
                        onChange={() => setIsChecked1(!isChecked1)}
                    />
                    <label htmlFor="colorCheckbox">{formatMessage({ id: 'color' })}</label>
                </div>
                <div className='d-flex' style={{ gap: '7px' }}>
                    <input
                        type="checkbox"
                        id="sizeCheckbox"
                        checked={isChecked2}
                        onChange={() => setIsChecked2(!isChecked2)}
                    />
                    <label htmlFor="sizeCheckbox">{formatMessage({ id: 'size' })}</label>
                </div>
            </div>

          {isChecked1 && (
            <div className="row mb-3">
                <label className="form-label fw-bold fs-4">
                    {formatMessage({ id: "color" })}
                </label>
                <div className="d-flex align-items-center gap-2">
                    <Select
                      options={availableColors}
                      onChange={handleColorSelectChange}
                      isSearchable={true}
                      placeholder="Select color"
                      className="basic-single"
                      classNamePrefix="select"
                      styles={{
                        container: (base: any) => ({
                          ...base,
                          width: '200px'
                        })
                      }}
                    />
                    <div className="d-flex flex-wrap gap-2" style={{ height: '40px' }}>
                        {selectedColors.map((colorId: string) => (
                          <span key={colorId} className="badge bg-light text-dark d-flex align-items-center">
                  {availableColors.find((c) => c.value.toString() === colorId)?.label}{" "}
                              <button
                                onClick={() => removeColor(colorId)}
                                className="btn-close btn-sm ms-2"
                                aria-label="Remove"
                              ></button>
                </span>
                        ))}
                    </div>
                </div>
            </div>
          )}

          {isChecked2 && (
            <div className="row mb-3">
                <label className="form-label fw-bold fs-3">
                    {formatMessage({ id: "size" })}
                </label>
                <div className="d-flex align-items-center gap-2">
                    <Select
                      options={availableSizes}
                      onChange={handleSizeSelectChange}
                      isSearchable={true}
                      placeholder="Select size"
                      className="basic-single"
                      classNamePrefix="select"
                      styles={{
                        container: (base: any) => ({
                          ...base,
                          width: '200px'
                        })
                      }}
                    />
                    <div className="d-flex flex-wrap gap-2" style={{ height: '40px' }}>
                        {selectedSizes.map((sizeId: string) => (
                          <span key={sizeId} className="badge bg-light text-dark d-flex align-items-center">
                  {availableSizes.find((s) => s.value.toString() === sizeId)?.label}{" "}
                              <button
                                onClick={() => removeSize(sizeId)}
                                className="btn-close btn-sm ms-2"
                                aria-label="Remove"
                              ></button>
                </span>
                        ))}
                    </div>
                </div>
            </div>
          )}

            { showTable &&
                <table className="variations-table">
                    <thead>
                        <tr>
                            <th>{formatMessage({ id: "color" })}</th>
                            <th>{formatMessage({ id: "size" })}</th>
                            <th>{formatMessage({ id: "barcode" })}</th>
                            <th>{formatMessage({ id: "images" })}</th>
                            <th>{formatMessage({ id: "price" })}</th>
                            <th>{formatMessage({ id: "quantity" })}</th>
                        </tr>
                    </thead>

                    <tbody>

                    {variations.map((variant, index) => (
                        <tr key={index} style={{height: '100px'}}>
                            <td>
                                <div className="d-flex gap-5">
                                    <img
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => removeVariation(index)}
                                        width="17"
                                        src={toAbsoluteUrl('/media/logos/remove.svg')}
                                    />
                                    {availableColors.find((color) => color.value == variant.color_id)?.label || formatMessage({ id: 'no_color' }) }
                                </div>
                            </td>
                            <td>
                                {availableSizes.find((size) => size.value == variant.size_id)?.label || formatMessage({ id: 'no_size' }) }
                            </td>
                            <td style={{width: '300px' }}>
                                <div className="d-flex gap-5">
                                    <input
                                        value={variant.barcode}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeBarcode(e, index)}
                                        id={`barcode-${index}`}
                                        required={true}
                                        style={{ border: '1px solid var(--kt-input-border-color)', borderRadius: '0.475rem' }}
                                    />
                                    <button
                                        onClick={() => generateBarcode(index)}
                                        className="btn btn-sm fs-5 px-3 btn-light btn-close-white generate-barcode-btn w-30"
                                    >
                                        {formatMessage({ id: 'generate' })}
                                    </button>
                                </div>
                            </td>
                            <td>
                                <img
                                    className='d-flex m-auto'
                                    style={{ cursor: 'pointer', width: '50px' }}
                                    width="60"
                                    onClick={() => openPopup(index)}
                                    src={
                                        prodState.variationImages[index]?.image?.value instanceof Blob
                                            ? URL.createObjectURL(prodState.variationImages[index].image.value)
                                            : (
                                                prodState.variationImages[index]?.image?.value?.trim()
                                                    ? prodState.variationImages[index]?.image?.value
                                                    : toAbsoluteUrl('/media/logos/img_upload.svg')
                                            )
                                    }
                                />

                                {isPopupVisible && activePopupIndex === index && (
                                    <div className="popup-container">
                                        <div className="popup-overlay" onClick={closePopup}></div>
                                        <div className="popup-content">
                                            <ProductMoreImagesVariations
                                                prodState={prodState}
                                                prodDispatch={prodDispatch}
                                                index={index}
                                                closePopup={closePopup}
                                            />
                                        </div>
                                    </div>
                                )}
                            </td>
                            <td className='text-align-center'>
                                <input
                                    type="number"
                                    value={variant.price}
                                    id={'price-variation'}
                                    onFocus={(e) => {
                                        if (e.target.value === '0') {
                                            e.target.value = '';
                                        }
                                    }}
                                    onChange={(e) => handleChange(e, index, 'price')}
                                    required={true}
                                    style={{ border: '1px solid var(--kt-input-border-color)', borderRadius: '0.475rem',
                                        width: '50px', height: '50px', textAlign: 'center'
                                    }}
                                />
                            </td>
                            <td className='text-align-center'>
                                <input
                                    type="number"
                                    min="0"
                                    value={variant.quantity}
                                    id={'quantity-variation'}
                                    onFocus={(e) => {
                                        if (e.target.value === '0') {
                                            e.target.value = '';
                                        }
                                    }}
                                    onChange={(e) => handleChange(e, index, 'quantity')}
                                    required={true}
                                    style={{ border: '1px solid var(--kt-input-border-color)', borderRadius: '0.475rem', width: '50px'
                                        , height: '50px', textAlign: 'center'}}
                                />
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            }
        </div>
    );
};

export default VariationsField;
